import {
  type ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Copy,
  Delete,
  ErrorFill,
} from "@xfrontend/xicon";
import {
  Accordion, Button, Modal, Spinner, Toggle,
} from "@xfrontend/xui-react-plus";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import ReactSelect from "react-select";
import styled from "styled-components";
import { useAuth } from "../../modules/auth/useAuth";
import { useTranslation } from "react-i18next";
import { RootState } from "#/types/RootState";
import { POLICY_DISPLAY_NAMES, sortByRiskLevelDesc } from "#/types/xecguard";
import { toast } from "#/infra/Toastify";
import {
  getPolicies, getPolicyDetail, updatePolicyRules, updatePolicy, createPolicy, deletePolicy,
} from "./slice";
import { fetchServiceTokens } from "#/infra/redux/actionCreator";
import { getPolicyNameMapping } from "#/modules/dashboard/redux/actionCreator";
import { PolicyItem } from "./types";
import PanelProvider from "#/modules/common/PanelProvider";
import CommonModal from "#/modules/common/CommonModal";
import ViolationPolicyTypeTag from "#/modules/common/ViolationPolicyTypeTag";
import PoliciesLeftPanel from "./components/PoliciesLeftPanel";
import PoliciesRightPanel from "./components/PoliciesRightPanel";

const PoliciesPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
  flex: 1;
  height: 100%;
  min-height: 0;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(0, 1fr) minmax(0, 1fr);
  }
`;

const PoliciesHeader = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 12px;
`;

const HeaderTitle = styled.div`
  color: var(--x-gray-200, #F6F6F7);
  font-size: 24px;
  font-family: Inter, sans-serif;
  font-weight: 700;
  line-height: 33.6px;
`;

const EditPolicyModal = styled(CommonModal)`
  .modal-dialog {
    width: 800px;
    max-width: calc(100% - 32px);
  }

  .modal-content {
    background: var(--x-bg-500, #2d293a);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 6px 24px rgba(0, 0, 0, 0.4);
  }

  .modal-header {
    background: var(--x-bg-400, #3b3649);
    padding: 16px;
  }

  .modal-body {
    padding: 18px 24px;
    border-bottom: 1px solid var(--x-theme-secondary, #73698a);
  }

  .modal-footer {
    padding: 16px;
  }
`;

const EditRuleStringsModal = styled(CommonModal)`
  .modal-dialog {
    width: 1138px;
    max-width: calc(100% - 32px);
  }

  .modal-content {
    background: var(--x-bg-500, #2d293a);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0px 6px 24px rgba(0, 0, 0, 0.4);
  }

  .modal-header {
    background: var(--x-bg-400, #3b3649);
    padding: 16px;
  }

  .modal-body {
    padding: 18px 24px;
    border-bottom: 1px solid var(--x-theme-secondary, #73698a);
  }

  .modal-footer {
    padding: 16px;
  }
`;

const EditPolicyTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--x-gray-200, #f6f6f7);
  margin-bottom: 12px;
`;

const RuleStringsTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  color: var(--x-gray-200, #f6f6f7);
`;

const RuleStringsInstruction = styled.div`
  font-size: 14px;
  color: var(--x-gray-400, #cfd0d3);
  line-height: 1.5;
`;

const CustomRulesGuidance = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 12px;
`;

const RuleStringsFormula = styled.div`
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: var(--x-gray-200, #f6f6f7);
  background: var(--x-dark-light, #494358);
  border-radius: 4px;
  padding: 8px 12px;
  line-height: 1.5;
`;

const RuleStringsLabel = styled.div`
  font-size: 14px;
  color: var(--x-gray-200, #f6f6f7);
  line-height: 1;
`;

const RuleStringsCharCount = styled.div`
  font-size: 12px;
  color: var(--x-gray-400, #cfd0d3);
  margin-top: 6px;
`;

const RuleStringsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RuleStringsGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 18px;
`;

const RuleStringsLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
`;

const RuleStringsTextBox = styled.div`
  position: relative;
  background: var(--x-purple-900, #0f0d12);
  border: 2px solid var(--x-theme-secondary, #494651);
  border-radius: 4px;
  padding: 9px 12px;
  flex: 1;
  display: flex;
  align-items: stretch;
`;

const RuleStringsTextArea = styled.textarea`
  flex: 1;
  border: 0;
  background: transparent;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;
  min-height: 0;
`;

const RuleStringsSuggestions = styled.div`
  background: var(--x-bg-700, #1f1d29);
  border-radius: 4px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SuggestionsTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
`;

const SuggestionsList = styled.ul`
  margin: 0;
  padding-left: 21px;
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  line-height: 1.5;
`;

const SuggestionsListItem = styled.li`
  margin-bottom: 0;
`;

const SchemaBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SchemaLabel = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--x-gray-200, #f6f6f7);
`;

const SchemaTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const FieldLabel = styled.div`
  font-size: 14px;
  color: var(--x-gray-200, #f6f6f7);
`;

const RequiredMark = styled.span`
  color: var(--x-red, #ff595a);
  margin-left: 4px;
`;

const InputBox = styled.div`
  background: var(--x-purple-700, #2c2835);
  border: 2px solid var(--x-theme-secondary, #73698a);
  border-radius: 4px;
  height: 32px;
  display: flex;
  align-items: center;
  padding: 0 12px;
`;

const TextInput = styled.input`
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1;
  outline: none;

  &::placeholder {
    color: var(--x-gray-500, #a9a9ac);
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ModalSelect = styled(ReactSelect as any)`
  .policy-select__control {
    background: var(--x-purple-700, #2c2835);
    border: 2px solid var(--x-theme-secondary, #73698a);
    border-radius: 4px;
    min-height: 32px;
    box-shadow: none;
    outline: none;

    &:focus,
    &:focus-within {
      border-color: var(--x-red, #ff595a);
      box-shadow: none;
      outline: none;
    }
  }

  .policy-select__value-container {
    padding: 0 12px;
  }

  .policy-select__indicator-separator {
    display: none;
  }

  .policy-select__indicator {
    padding-right: 8px;
  }

  .policy-select__single-value {
    color: var(--x-gray-200, #f6f6f7);
    font-size: 14px;
    line-height: 1;
  }

  .policy-select__input-container,
  .policy-select__placeholder {
    color: var(--x-gray-500, #a9a9ac);
    font-size: 14px;
    line-height: 1;
  }

  .policy-select__menu {
    background: var(--x-purple-500, #494358);
    border-radius: 4px;
    padding: 4px;
    box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.3);
  }

  .policy-select__option {
    padding: 8px;
    border-radius: 4px;
    background: transparent !important;
    cursor: pointer;
  }
`;

const TextAreaBox = styled.div`
  position: relative;
  display: flex;
  background: var(--x-purple-900, #0f0d12);
  border: 2px solid var(--x-theme-secondary, #494651);
  border-radius: 4px;
  padding: 9px 12px;
  min-height: 115px;
`;

const TextArea = styled.textarea`
  flex: 1;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;

  &::placeholder {
    color: var(--x-gray-500, #a9a9ac);
  }
`;

const CopyIconButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const RuleTextAreaBox = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  background: var(--x-purple-900, #0f0d12);
  border: 2px solid var(--x-theme-secondary, #494651);
  border-radius: 4px;
  padding: 9px 12px;
  min-height: 160px;
`;

const RuleTextArea = styled.textarea`
  flex: 1;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  outline: none;

  &::placeholder {
    color: var(--x-gray-500, #a9a9ac);
  }
`;

const EnabledRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const EnabledLabel = styled.span`
  font-size: 14px;
  color: var(--x-gray-200, #f6f6f7);
`;

const PolicyRulesSectionTitle = styled.div`
  font-size: 14px;
  color: var(--x-gray-400, #cfd0d3);
  margin-bottom: 12px;
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background: var(--x-theme-warning, #846ce6);
  margin-bottom: 18px;
`;

const WarningText = styled.div`
  flex: 1;
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1.25;
`;

const PatternDescription = styled.div`
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  line-height: 1.5;
`;

const PatternInputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  width: 100%;
  max-width: 497px;
`;

const PatternField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
`;

const PatternInput = styled.input`
  width: 100%;
  height: 32px;
  border-radius: 4px;
  border: 2px solid var(--x-theme-secondary, #73698a);
  background: transparent;
  color: var(--x-gray-200, #f6f6f7);
  padding: 0 12px;
  font-size: 14px;
  line-height: 1;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: var(--x-gray-500, #a9a9ac);
  }
`;

const PatternAddButton = styled.button`
  height: 32px;
  border-radius: 4px;
  border: 2px solid var(--x-red, #ff595a);
  background: var(--x-red, #ff595a);
  color: var(--x-gray-200, #f6f6f7);
  padding: 0 16px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PatternLimitText = styled.div`
  color: var(--x-gray-400, #cfd0d3);
  font-size: 14px;
  line-height: 1.5;
`;

const PatternList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  overflow: hidden;
  border-radius: 4px;
  width: 100%;
`;

const PatternRuleRow = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--x-purple-500, #3b3649);
  padding-left: 9px;
`;

const PatternRuleValue = styled.div`
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  line-height: 1.5;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PatternDeleteButton = styled.button`
  width: 40px;
  height: 40px;
  border: 0;
  background: transparent;
  color: var(--x-gray-400, #cfd0d3);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    color: var(--x-red, #ff595a);
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const PatternEmptyState = styled.div`
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 0 9px;
  background: var(--x-purple-500, #3b3649);
  color: var(--x-gray-500, #a9a9ac);
  font-size: 14px;
  line-height: 1.5;
`;

const ModalRulesAccordion = styled(Accordion)`
  .accordion-item {
    border: 0;
    border-radius: 0;
    overflow: hidden;
  }

  .accordion-button {
    background: var(--x-purple-500, #494358);
    border: 0;
    border-bottom: 1px solid var(--x-purple-400, #6a6280);
    box-shadow: none;
    height: 45px;
    padding: 0 12px;
    border-radius: 0;
    color: var(--x-gray-200, #f6f6f7);
    font-size: 14px;
    line-height: 1.5;

    &::after {
      margin-left: 9px;
    }
  }

  .accordion-body {
    background: var(--x-purple-600, #3a3646);
    border-bottom: 1px solid var(--x-purple-400, #6a6280);
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .accordion-item:last-of-type .accordion-body {
    border-bottom: 0;
  }
`;

const ModalRulesAccordionWrap = styled.div`
  width: 100%;
  background: var(--x-purple-600, #3a3646);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
`;

type ThreatLevelOption = {
  value: number;
  label: string;
};

type PolicyTypeValue = "semantic_text" | "pattern_keyword" | "pattern_regex";

type PolicyTypeOption = {
  value: PolicyTypeValue;
  label: string;
};

type PolicyFormSnapshot = {
  policyName: string;
  threatLevel: number;
  description: string;
  policyType: PolicyTypeValue;
  rulesList: string[];
  enabled: boolean;
};

const normalizePolicyType = (value?: string): PolicyTypeValue => {
  if (value === "pattern_keyword") return "pattern_keyword";
  if (value === "pattern_regex" || value === "pattern") return "pattern_regex";
  return "semantic_text";
};

const normalizePolicyText = (value?: string) => value?.trim() ?? "";

const normalizePolicyRules = (rulesList: string[]) => rulesList
  .map((rule) => rule.trim())
  .filter((rule) => rule !== "");

const buildPolicyFormSnapshot = ({
  policyName,
  threatLevel,
  description,
  policyType,
  rulesList,
  enabled,
}: PolicyFormSnapshot): PolicyFormSnapshot => ({
  policyName: normalizePolicyText(policyName),
  threatLevel,
  description: description ?? "",
  policyType,
  rulesList: normalizePolicyRules(rulesList),
  enabled,
});

const isSamePolicyFormSnapshot = (
  current: PolicyFormSnapshot | null,
  initial: PolicyFormSnapshot | null,
) => JSON.stringify(current) === JSON.stringify(initial);

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const buildThreatLevelOptions = (t: TFunction): ThreatLevelOption[] => [
  { value: 4, label: t("threatLevel.high") },
  { value: 3, label: t("threatLevel.medium") },
  { value: 2, label: t("threatLevel.low") },
  { value: 1, label: t("threatLevel.minimal") },
];

const buildPolicyTypeOptions = (t: TFunction): PolicyTypeOption[] => [
  { value: "semantic_text", label: t("policyType.semanticText") },
  { value: "pattern_keyword", label: t("policyType.patternKeyword") },
  { value: "pattern_regex", label: t("policyType.patternRegex") },
];

const MAX_PATTERN_RULES = 10;

const getPatternPolicyCopy = (
  type: Exclude<PolicyTypeValue, "semantic_text">,
  t: TFunction,
): {
  description: string;
  inputLabel: string;
  placeholder: string;
  limitLabel: string;
  emptyState: string;
} => {
  if (type === "pattern_keyword") {
    return {
      description: t("pattern.keyword.description"),
      inputLabel: t("pattern.keyword.inputLabel"),
      placeholder: t("pattern.keyword.placeholder"),
      limitLabel: t("pattern.keyword.limitLabel"),
      emptyState: t("pattern.keyword.emptyState"),
    };
  }

  return {
    description: t("pattern.regex.description"),
    inputLabel: t("pattern.regex.inputLabel"),
    placeholder: t("pattern.regex.placeholder"),
    limitLabel: t("pattern.regex.limitLabel"),
    emptyState: t("pattern.regex.emptyState"),
  };
};

const Policies = (): JSX.Element => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const { t } = useTranslation("policySpec");
  const threatLevelOptions = useMemo(() => buildThreatLevelOptions(t), [t]);
  const policyTypeOptions = useMemo(() => buildPolicyTypeOptions(t), [t]);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [isEditPolicyOpen, setIsEditPolicyOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isEditRuleStringsOpen, setIsEditRuleStringsOpen] = useState(false);
  const [isCreateRuleMode, setIsCreateRuleMode] = useState(false);
  const [policyName, setPolicyName] = useState("");
  const [threatLevel, setThreatLevel] = useState<ThreatLevelOption>(
    threatLevelOptions[2],
  );
  const [policyType, setPolicyType] = useState<PolicyTypeValue>("semantic_text");
  const [initialPolicyType, setInitialPolicyType] = useState<PolicyTypeValue>("semantic_text");
  const [description, setDescription] = useState("");
  const [rules, setRules] = useState<string[]>([""]);
  const [patternRuleInput, setPatternRuleInput] = useState("");
  const [patternRules, setPatternRules] = useState<string[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [initialFormState, setInitialFormState] = useState<PolicyFormSnapshot | null>(null);
  const [ruleStrings, setRuleStrings] = useState("");
  const [editingRuleIndex, setEditingRuleIndex] = useState<number | null>(null);
  const [isDeletePolicyModalOpen, setIsDeletePolicyModalOpen] = useState(false);

  const policiesLoading = useSelector((state: RootState) => state.policies?.list?.loading);
  const policiesData = useSelector((state: RootState) => state.policies?.list?.data);
  const policyDetailLoading = useSelector((state: RootState) => state.policies?.detail?.loading);
  const policyDetailData = useSelector((state: RootState) => state.policies?.detail?.data);
  const serviceTokensData = useSelector(
    (state: RootState) => state.infra.serviceTokens.results,
  ) ?? [];
  const policyNameMapping = useSelector(
    (state: RootState) => state.dashboard.policyNameMapping.results,
  );
  useEffect(() => {
    if (auth.isAuthenticated && auth.user?.access_token) {
      dispatch(getPolicies({ accessToken: auth.user.access_token, period: 7 }));
      dispatch(fetchServiceTokens(auth.user.access_token));
      dispatch(getPolicyNameMapping(auth.user.access_token));
    }
  }, [auth.isAuthenticated, auth.user?.access_token, dispatch]);

  const policies: PolicyItem[] = useMemo(
    () => (policiesData ?? []).map((p) => ({
      id: p.policy_id,
      name: policyNameMapping?.[p.policy_name] ?? POLICY_DISPLAY_NAMES[p.policy_name] ?? p.policy_name,
      tag: p.policy_id.startsWith("default_") ? "Default" : "Custom",
      policyName: p.policy_name,
      currentVersionId: p.current_version_id ?? "",
      createdAt: p.created_at ?? "",
      enabled: p.policy_status !== "disable",
      applied: p.tokenwise_data?.length ?? 0,
      riskLevel: p.risk_level ?? 2,
      threats: p.summary?.request_unsafe_count ?? 0,
      checks: p.summary?.request_count ?? 0,
      description: p.description ?? "",
      daywiseData: p.daywise_data ?? [],
      tokenwiseData: p.tokenwise_data ?? [],
    })),
    [policiesData, policyNameMapping],
  );

  const queryLower = query.trim().toLowerCase();
  const filteredDefaultPolicies = policies
    .filter((p) => p.tag === "Default" && p.name.toLowerCase().includes(queryLower))
    .sort(sortByRiskLevelDesc);
  const filteredCustomPolicies = policies
    .filter((p) => p.tag === "Custom" && p.name.toLowerCase().includes(queryLower))
    .sort(sortByRiskLevelDesc);

  useEffect(() => {
    if (!selectedId && policies.length > 0) {
      setSelectedId(policies[0].id);
    }
  }, [policies, selectedId]);

  const selectedPolicy = policies.find((policy) => policy.id === selectedId) ?? policies[0];

  useEffect(() => {
    if (
      selectedPolicy?.tag === "Custom"
      && selectedPolicy?.id
      && auth.isAuthenticated
      && auth.user?.access_token
    ) {
      dispatch(getPolicyDetail({
        accessToken: auth.user.access_token,
        policyId: selectedPolicy.id,
      }));
    }
  }, [selectedPolicy?.id, selectedPolicy?.tag, auth.isAuthenticated]);

  const policyRulesList = selectedPolicy?.tag === "Custom"
    && policyDetailData?.policy_id === selectedPolicy?.id
    ? policyDetailData?.rules_list ?? []
    : [];
  const currentSelectedPolicyType = (
    policyDetailData?.policy_id
    && selectedPolicy?.id
    && policyDetailData.policy_id === selectedPolicy.id
  )
    ? normalizePolicyType(policyDetailData.policy_type)
    : undefined;

  const selectedPolicyType = policyTypeOptions.find((option) => option.value === policyType) ?? policyTypeOptions[0];
  const isPatternPolicy = policyType !== "semantic_text";
  const hasPolicyTypeChanged = !isCreateMode && policyType !== initialPolicyType;
  const patternPolicyCopy = policyType !== "semantic_text" ? getPatternPolicyCopy(policyType, t) : null;
  const semanticRulesList = rules.filter((rule) => rule.trim() !== "");
  const activeRulesList = isPatternPolicy ? patternRules : semanticRulesList;
  const currentFormState = buildPolicyFormSnapshot({
    policyName,
    threatLevel: threatLevel.value,
    description,
    policyType,
    rulesList: activeRulesList,
    enabled,
  });
  const canCreatePolicy = currentFormState.policyName !== "" && currentFormState.rulesList.length > 0;
  const hasPolicyFormChanged = !isCreateMode
    && !isSamePolicyFormSnapshot(currentFormState, initialFormState);
  const canUpdatePolicy = canCreatePolicy && hasPolicyFormChanged;

  const openCreatePolicyModal = () => {
    const nextThreatLevel = threatLevelOptions.find((opt) => opt.value === 2) ?? threatLevelOptions[2];
    setPolicyName("");
    setThreatLevel(nextThreatLevel);
    setPolicyType("semantic_text");
    setInitialPolicyType("semantic_text");
    setDescription("");
    setRules(["", "", ""]);
    setPatternRuleInput("");
    setPatternRules([]);
    setEnabled(true);
    setInitialFormState(buildPolicyFormSnapshot({
      policyName: "",
      threatLevel: nextThreatLevel.value,
      description: "",
      policyType: "semantic_text",
      rulesList: [],
      enabled: true,
    }));
    setIsCreateMode(true);
    setIsEditPolicyOpen(true);
  };

  const openEditPolicyModal = () => {
    if (policyDetailData && policyDetailData.policy_id === selectedPolicy?.id) {
      setPolicyName(policyDetailData.policy_name ?? "");
      const matchedLevel = threatLevelOptions.find(
        (opt) => opt.value === policyDetailData.risk_level,
      );
      setThreatLevel(matchedLevel ?? threatLevelOptions[2]);
      const nextPolicyType = normalizePolicyType(policyDetailData.policy_type);
      setPolicyType(nextPolicyType);
      setInitialPolicyType(nextPolicyType);
      setDescription(policyDetailData.description ?? "");
      const existingRules = policyDetailData.rules_list ?? [];
      if (nextPolicyType === "pattern_keyword" || nextPolicyType === "pattern_regex") {
        setPatternRules(existingRules);
        setPatternRuleInput("");
        setRules(["", "", ""]);
      } else {
        const padded = [...existingRules];
        while (padded.length < 3) padded.push("");
        setRules(padded);
        setPatternRules([]);
        setPatternRuleInput("");
      }
      setEnabled(policyDetailData.policy_status === "enable");
      setInitialFormState(buildPolicyFormSnapshot({
        policyName: policyDetailData.policy_name ?? "",
        threatLevel: (matchedLevel ?? threatLevelOptions[2]).value,
        description: policyDetailData.description ?? "",
        policyType: nextPolicyType,
        rulesList: existingRules,
        enabled: policyDetailData.policy_status === "enable",
      }));
    } else if (selectedPolicy) {
      const matchedLevel = threatLevelOptions.find(
        (opt) => opt.value === selectedPolicy.riskLevel,
      );
      setPolicyName(selectedPolicy.name ?? "");
      setThreatLevel(matchedLevel ?? threatLevelOptions[2]);
      setPolicyType("semantic_text");
      setInitialPolicyType("semantic_text");
      setDescription(selectedPolicy.description ?? "");
      setRules(["", "", ""]);
      setPatternRules([]);
      setPatternRuleInput("");
      setEnabled(true);
      setInitialFormState(buildPolicyFormSnapshot({
        policyName: selectedPolicy.name ?? "",
        threatLevel: (matchedLevel ?? threatLevelOptions[2]).value,
        description: selectedPolicy.description ?? "",
        policyType: "semantic_text",
        rulesList: [],
        enabled: true,
      }));
    }
    setIsCreateMode(false);
    setIsEditPolicyOpen(true);
  };

  useEffect(() => {
    if (isCreateMode || !isEditPolicyOpen || !selectedPolicy?.id) return;
    if (!policyDetailData || policyDetailData.policy_id !== selectedPolicy.id) return;

    const nextPolicyType = normalizePolicyType(policyDetailData.policy_type);
    setPolicyType(nextPolicyType);
    setInitialPolicyType(nextPolicyType);
  }, [
    isCreateMode,
    isEditPolicyOpen,
    policyDetailData,
    selectedPolicy?.id,
  ]);

  const handleSavePolicy = () => {
    if (!auth.user?.access_token) return;
    const rulesList = isPatternPolicy ? patternRules : semanticRulesList;
    if (isCreateMode) {
      dispatch(createPolicy({
        accessToken: auth.user.access_token,
        policyName,
        description,
        riskLevel: threatLevel.value,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        serviceTokenIds: serviceTokensData.map((t: any) => t.service_token_id),
        policyType,
        rulesList,
        enabled,
      }));
    } else if (selectedPolicy?.id) {
      dispatch(updatePolicy({
        accessToken: auth.user.access_token,
        policyId: selectedPolicy.id,
        policyName,
        description,
        riskLevel: threatLevel.value,
        policyType,
        rulesList,
        enabled,
      }));
    }
    setIsEditPolicyOpen(false);
  };

  const handleCopyText = async (value: string) => {
    if (!value || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("toast.copied"));
    } catch {
      toast.error(t("toast.copyFailed"));
    }
  };

  const handleRuleChange = (index: number, value: string) => {
    setRules((prev) => prev.map((r, i) => (i === index ? value : r)));
  };

  const handleAddPatternRule = () => {
    const nextValue = patternRuleInput.trim();
    if (!nextValue) return;
    if (patternRules.length >= MAX_PATTERN_RULES) return;
    if (patternRules.includes(nextValue)) {
      toast.error(t("toast.ruleExists"));
      return;
    }
    if (policyType === "pattern_regex") {
      try {
        // Validate regex syntax before adding it to the draft list.
        // eslint-disable-next-line no-new
        new RegExp(nextValue);
      } catch {
        toast.error(t("toast.invalidRegex"));
        return;
      }
    }
    setPatternRules([...patternRules, nextValue]);
    setPatternRuleInput("");
  };

  const handleDeletePatternRule = (index: number) => {
    setPatternRules((prev) => prev.filter((ruleValue, ruleIndex) => ruleIndex !== index));
  };

  const handlePolicyTypeChange = (nextType: PolicyTypeValue) => {
    if (nextType === policyType) return;

    setPolicyType(nextType);
    setPatternRuleInput("");

    if (nextType === "semantic_text") {
      setRules(["", "", ""]);
      return;
    }

    setPatternRules([]);
  };

  const openCreateRuleStringsModal = () => {
    setRuleStrings("");
    setEditingRuleIndex(null);
    setIsCreateRuleMode(true);
    setIsEditRuleStringsOpen(true);
  };

  const openEditRuleStringsModal = (index: number) => {
    setRuleStrings(policyRulesList[index] ?? "");
    setEditingRuleIndex(index);
    setIsCreateRuleMode(false);
    setIsEditRuleStringsOpen(true);
  };

  const handleDeletePolicy = () => {
    setIsDeletePolicyModalOpen(true);
  };

  const confirmDeletePolicy = () => {
    if (selectedPolicy?.id && auth.user?.access_token) {
      dispatch(deletePolicy({
        accessToken: auth.user.access_token,
        policyId: selectedPolicy.id,
        policyName: selectedPolicy.name,
      }));
      setSelectedId("");
    }
    setIsDeletePolicyModalOpen(false);
  };

  const handleDeleteRuleFromApi = (index: number) => {
    if (selectedPolicy?.id && auth.user?.access_token) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const updatedRules = policyRulesList.filter((_, i) => i !== index);
      dispatch(updatePolicyRules({
        accessToken: auth.user.access_token,
        policyId: selectedPolicy.id,
        rulesList: updatedRules,
      }));
    }
  };

  const handleAddPatternRuleToApi = (value: string) => {
    const nextValue = value.trim();
    if (!nextValue || !selectedPolicy?.id || !auth.user?.access_token || !currentSelectedPolicyType) return false;
    if (policyRulesList.length >= MAX_PATTERN_RULES) return false;
    if (policyRulesList.includes(nextValue)) {
      toast.error(t("toast.ruleExists"));
      return false;
    }
    if (currentSelectedPolicyType === "pattern_regex") {
      try {
        // Validate regex syntax before sending it to the API.
        // eslint-disable-next-line no-new
        new RegExp(nextValue);
      } catch {
        toast.error(t("toast.invalidRegex"));
        return false;
      }
    }

    dispatch(updatePolicyRules({
      accessToken: auth.user.access_token,
      policyId: selectedPolicy.id,
      rulesList: [...policyRulesList, nextValue],
    }));
    return true;
  };

  const handleSaveRuleStrings = () => {
    if (selectedPolicy?.id && auth.user?.access_token) {
      const updatedRules = [...policyRulesList];
      if (isCreateRuleMode) {
        if (ruleStrings.trim()) {
          updatedRules.push(ruleStrings.trim());
        }
      } else if (editingRuleIndex !== null) {
        updatedRules[editingRuleIndex] = ruleStrings.trim();
      }
      dispatch(updatePolicyRules({
        accessToken: auth.user.access_token,
        policyId: selectedPolicy.id,
        rulesList: updatedRules.filter((r) => r !== ""),
      }));
    }
    setIsEditRuleStringsOpen(false);
  };

  const actionTags = ["Forbid", "Reject", "Block", "Deny"];
  const scopeTags = ["all user prompts", "any queries"];
  const targetBehaviorTags = [
    "requesting to process", "validate",
    "requesting to reset", "ignore",
  ];
  const targetObjectTags = [
    "any text containing person names", "encoded messages",
  ];
  const conditionTags = [
    "regardless of the intent",
    "regardless of how the user phrases the request",
  ];
  const structureGuidelines = [
    "Conciseness is key to high-performance rules."
    + " Each Rule should focus on a single,"
    + " independent logical statement.",
    "Single Responsibility Principle: A rule should"
    + " focus on defending against one type of risk"
    + " only. Avoid combining multiple unrelated"
    + " logics into a single lengthy rule.",
    "Structural Recommendation: Best practice is to"
    + " express each rule as a complete single"
    + " sentence. Paragraph-style rules consisting of"
    + " multiple sentences significantly increase"
    + " parsing ambiguity and should be split into"
    + " multiple independent rules.",
    "Length Guidance: Effective rules are typically"
    + " concise. If a rule's description becomes"
    + " overly lengthy (e.g., exceeding 50-70 words),"
    + " this usually indicates it should be refactored"
    + " into several more specific, focused rules.",
  ];

  return (
    <PanelProvider style={{ overflow: "hidden" }}>
      <div
        style={{
          padding: "12px",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <PoliciesPanel>
          <PoliciesHeader>
            <HeaderTitle>{t("pageTitle")}</HeaderTitle>
            <Button variant="primary" onClick={openCreatePolicyModal}>
              {t("createButton")}
            </Button>
          </PoliciesHeader>
          {policiesLoading ? (
            <div style={{
              display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0",
            }}
            >
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <ContentGrid>
              <PoliciesLeftPanel
                defaultPolicies={filteredDefaultPolicies}
                customPolicies={filteredCustomPolicies}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
                query={query}
                setQuery={setQuery}
                searchInputRef={searchInputRef}
              />
              <PoliciesRightPanel
                selectedPolicy={selectedPolicy}
                serviceTokens={serviceTokensData ?? []}
                policyType={currentSelectedPolicyType}
                rulesList={policyRulesList}
                rulesLoading={!!policyDetailLoading}
                onEditPolicy={openEditPolicyModal}
                onCreateRule={openCreateRuleStringsModal}
                onEditRuleStrings={openEditRuleStringsModal}
                onAddPatternRule={handleAddPatternRuleToApi}
                onDeleteRule={handleDeleteRuleFromApi}
                onDeletePolicy={handleDeletePolicy}
              />
            </ContentGrid>
          )}
        </PoliciesPanel>
        <EditPolicyModal
          show={isEditPolicyOpen}
          setShow={setIsEditPolicyOpen}
          restoreFocus={false}
          size="lg"
          title={isCreateMode ? t("modal.createTitle") : t("modal.editTitle")}
          cancelText={t("modal.cancel")}
          submitText={isCreateMode ? t("modal.create") : t("modal.confirm")}
          submitVariant="primary"
          disablePrimaryButton={isCreateMode ? !canCreatePolicy : !canUpdatePolicy}
          onSubmit={handleSavePolicy}
        >
          <EditPolicyTitle>{isCreateMode ? t("modal.createSubtitle") : t("modal.editSubtitle")}</EditPolicyTitle>
          <FieldGroup>
            <FieldLabel>
              {t("form.policyName")}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <InputBox>
              <TextInput
                placeholder={t("form.policyNamePlaceholder")}
                value={policyName}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setPolicyName(event.target.value);
                }}
              />
            </InputBox>
          </FieldGroup>
          <FieldGroup style={{ maxWidth: 298 }}>
            <FieldLabel>
              {t("form.threatLevelLabel")}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <ModalSelect
              classNamePrefix="policy-select"
              isSearchable={false}
              options={threatLevelOptions}
              value={threatLevel}
              onChange={(selected: unknown) => {
                setThreatLevel(
                  (selected as ThreatLevelOption) ?? threatLevelOptions[0],
                );
              }}
            />
          </FieldGroup>
          <FieldGroup>
            <FieldLabel>{t("form.description")}</FieldLabel>
            <TextAreaBox>
              <TextArea
                placeholder=" "
                value={description}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setDescription(event.target.value);
                }}
              />
              <CopyIconButton
                type="button"
                aria-label={t("aria.copyDescription")}
                onClick={() => handleCopyText(description)}
              >
                <Copy size={14} color="var(--x-gray-200, #f6f6f7)" />
              </CopyIconButton>
            </TextAreaBox>
          </FieldGroup>
          {!isCreateMode && (
            <EnabledRow>
              <Toggle
                name="policy-enabled"
                type="switch"
                checked={enabled}
                onChange={() => setEnabled((v) => !v)}
              />
              <EnabledLabel>{t("form.enabled")}</EnabledLabel>
            </EnabledRow>
          )}
          <PolicyRulesSectionTitle>{t("form.policyRules")}</PolicyRulesSectionTitle>
          <FieldGroup style={{ maxWidth: 298 }}>
            <FieldLabel>
              {t("form.policyType")}
              <RequiredMark>*</RequiredMark>
            </FieldLabel>
            <ModalSelect
              classNamePrefix="policy-select"
              isSearchable={false}
              options={policyTypeOptions}
              value={selectedPolicyType}
              onChange={(selected: unknown) => {
                handlePolicyTypeChange(
                  (selected as PolicyTypeOption)?.value ?? "semantic_text",
                );
              }}
            />
          </FieldGroup>
          {hasPolicyTypeChanged && (
            <WarningBanner>
              <ErrorFill size={20} color="var(--x-gray-200, #f6f6f7)" />
              <WarningText>
                {t("warning.policyTypeChange")}
              </WarningText>
            </WarningBanner>
          )}
          {isPatternPolicy && patternPolicyCopy && (
            <>
              <PatternDescription>{patternPolicyCopy.description}</PatternDescription>
              <div style={{ height: 18 }} />
              <PatternInputRow>
                <PatternField>
                  <FieldLabel>
                    {patternPolicyCopy.inputLabel}
                    <RequiredMark>*</RequiredMark>
                  </FieldLabel>
                  <PatternInput
                    placeholder={patternPolicyCopy.placeholder}
                    value={patternRuleInput}
                    maxLength={255}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                      setPatternRuleInput(event.target.value);
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleAddPatternRule();
                      }
                    }}
                  />
                </PatternField>
                <PatternAddButton
                  type="button"
                  disabled={!patternRuleInput.trim() || patternRules.length >= MAX_PATTERN_RULES}
                  onClick={handleAddPatternRule}
                >
                  {t("pattern.addButton")}
                </PatternAddButton>
              </PatternInputRow>
              <div style={{ height: 12 }} />
              <PatternLimitText>
                {patternPolicyCopy.limitLabel}
                {" "}
                {`${patternRules.length}/${MAX_PATTERN_RULES}`}
              </PatternLimitText>
              <div style={{ height: 12 }} />
              <PatternList>
                {patternRules.length > 0 ? patternRules.map((rule, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <PatternRuleRow key={`${rule}-${index}`}>
                    <PatternRuleValue title={rule}>{rule}</PatternRuleValue>
                    <PatternDeleteButton
                      type="button"
                      aria-label={t("pattern.deleteAria", { rule })}
                      onClick={() => handleDeletePatternRule(index)}
                    >
                      <Delete size={14} color="currentColor" />
                    </PatternDeleteButton>
                  </PatternRuleRow>
                )) : (
                  <PatternEmptyState>{patternPolicyCopy.emptyState}</PatternEmptyState>
                )}
              </PatternList>
            </>
          )}
          {!isPatternPolicy && (
            <>
              <CustomRulesGuidance>
                <RuleStringsInstruction>
                  {t("customRules.instruction")}
                </RuleStringsInstruction>
                <RuleStringsFormula>
                  &quot;Action&quot; + &quot;Scope&quot;
                  {" + "}
                  &quot;Target Behavior&quot;
                  {" + "}
                  &quot;Target Object&quot;
                  {" + "}
                  &quot;Condition (Optional)&quot;
                </RuleStringsFormula>
              </CustomRulesGuidance>
              <ModalRulesAccordionWrap>
                <ModalRulesAccordion defaultActiveKey={["0"]} alwaysOpen>
                  {rules.map((rule, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Accordion.Item eventKey={String(index)} key={index}>
                      <Accordion.Header>
                        <div style={{ width: "100%" }}>
                          <span>{t("customRules.ruleAccordion", { num: index + 1 })}</span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <RuleTextAreaBox>
                          <RuleTextArea
                            placeholder={t("customRules.rulePlaceholder")}
                            value={rule}
                            onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                              handleRuleChange(index, event.target.value);
                            }}
                            maxLength={400}
                          />
                        </RuleTextAreaBox>
                        <RuleStringsCharCount>
                          {`${rule.length}/400`}
                        </RuleStringsCharCount>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </ModalRulesAccordion>
              </ModalRulesAccordionWrap>
            </>
          )}
        </EditPolicyModal>
        <EditRuleStringsModal
          show={isEditRuleStringsOpen}
          setShow={setIsEditRuleStringsOpen}
          restoreFocus={false}
          size="lg"
          title={t("ruleStringsModal.title")}
          cancelText={t("modal.cancel")}
          submitText={t("ruleStringsModal.save")}
          submitVariant="primary"
          onSubmit={handleSaveRuleStrings}
        >
          <RuleStringsContent>
            <RuleStringsTitle>{t("ruleStringsModal.subtitle")}</RuleStringsTitle>
            <RuleStringsGrid>
              <RuleStringsLeft>
                <RuleStringsInstruction>
                  {t("customRules.instruction")}
                </RuleStringsInstruction>
                <RuleStringsFormula>
                  &quot;Action&quot; + &quot;Scope&quot;
                  {" + "}
                  &quot;Target Behavior&quot;
                  {" + "}
                  &quot;Target Object&quot;
                  {" + "}
                  &quot;Condition (Optional)&quot;
                </RuleStringsFormula>
                <RuleStringsLabel>
                  {t("ruleStringsModal.ruleStringsLabel")}
                </RuleStringsLabel>
                <RuleStringsTextBox>
                  <RuleStringsTextArea
                    value={ruleStrings}
                    onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                      setRuleStrings(event.target.value);
                    }}
                    maxLength={400}
                  />
                  <CopyIconButton type="button" aria-label={t("aria.copyRuleStrings")}>
                    <Copy size={14} color="var(--x-gray-200, #f6f6f7)" />
                  </CopyIconButton>
                </RuleStringsTextBox>
                <RuleStringsCharCount>
                  {t("ruleStringsModal.maxLength", { current: ruleStrings.length })}
                </RuleStringsCharCount>
              </RuleStringsLeft>
              <RuleStringsSuggestions>
                <SuggestionsTitle>{t("ruleStringsModal.structureGuidelinesTitle")}</SuggestionsTitle>
                <SuggestionsList>
                  {structureGuidelines.map((text) => (
                    <SuggestionsListItem key={text.slice(0, 20)}>
                      {text}
                    </SuggestionsListItem>
                  ))}
                </SuggestionsList>
                <SuggestionsTitle>{t("ruleStringsModal.recommendedSchema")}</SuggestionsTitle>
                <SchemaBlock>
                  <SchemaLabel>{t("ruleStringsModal.schema.action")}</SchemaLabel>
                  <SchemaTags>
                    {actionTags.map((tag) => (
                      <ViolationPolicyTypeTag key={`action-${tag}`} index={3}>
                        {tag}
                      </ViolationPolicyTypeTag>
                    ))}
                  </SchemaTags>
                </SchemaBlock>
                <SchemaBlock>
                  <SchemaLabel>{t("ruleStringsModal.schema.scope")}</SchemaLabel>
                  <SchemaTags>
                    {scopeTags.map((tag) => (
                      <ViolationPolicyTypeTag key={`scope-${tag}`} index={3}>
                        {tag}
                      </ViolationPolicyTypeTag>
                    ))}
                  </SchemaTags>
                </SchemaBlock>
                <SchemaBlock>
                  <SchemaLabel>{t("ruleStringsModal.schema.targetBehavior")}</SchemaLabel>
                  <SchemaTags>
                    {targetBehaviorTags.map((tag) => (
                      <ViolationPolicyTypeTag key={`behavior-${tag}`} index={3}>
                        {tag}
                      </ViolationPolicyTypeTag>
                    ))}
                  </SchemaTags>
                </SchemaBlock>
                <SchemaBlock>
                  <SchemaLabel>{t("ruleStringsModal.schema.targetObject")}</SchemaLabel>
                  <SchemaTags>
                    {targetObjectTags.map((tag) => (
                      <ViolationPolicyTypeTag key={`object-${tag}`} index={3}>
                        {tag}
                      </ViolationPolicyTypeTag>
                    ))}
                  </SchemaTags>
                </SchemaBlock>
                <SchemaBlock>
                  <SchemaLabel>{t("ruleStringsModal.schema.condition")}</SchemaLabel>
                  <SchemaTags>
                    {conditionTags.map((tag) => (
                      <ViolationPolicyTypeTag key={`condition-${tag}`} index={3}>
                        {tag}
                      </ViolationPolicyTypeTag>
                    ))}
                  </SchemaTags>
                </SchemaBlock>
              </RuleStringsSuggestions>
            </RuleStringsGrid>
          </RuleStringsContent>
        </EditRuleStringsModal>
        <Modal
          title={t("delete.title")}
          show={isDeletePolicyModalOpen}
          onClose={() => setIsDeletePolicyModalOpen(false)}
          showCloseBtn
          restoreFocus={false}
        >
          <Modal.Body>
            {t("delete.confirm", { name: selectedPolicy?.name ?? "" })}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setIsDeletePolicyModalOpen(false)}>
              {t("delete.cancel")}
            </Button>
            <Button variant="primary" onClick={confirmDeletePolicy}>
              {t("delete.deleteBtn")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </PanelProvider>
  );
};

export default Policies;
